import { useEffect, useState } from 'react';
import { Info, RotateCcw, Save, Settings } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';

/**
 * System Admin › Subscriptions, top card (RESTORE_PLAN R3.2, carrying the old
 * SystemConfigSettings): the global company-limit multiplier every firm uses
 * unless it has its own override (set per firm in Consulting Firms). Active
 * companies allowed = users × multiplier. `GET/POST /admin/company-limit-multiplier`.
 */

const ROUTE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/company-limit-multiplier`;
export const DEFAULT_MULTIPLIER = 5;

/** The worked examples under the input: each plan's included seats × the multiplier. */
export const MULTIPLIER_EXAMPLES: { plan: string; users: number }[] = [
  { plan: 'Solo', users: 1 },
  { plan: 'Starter', users: 10 },
  { plan: 'Premium', users: 25 },
  { plan: 'Enterprise', users: 100 },
];

export function isValidMultiplier(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= 100;
}

export function CompanyLimitMultiplierSettings({ accessToken }: { accessToken: string }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [multiplier, setMultiplier] = useState<number>(DEFAULT_MULTIPLIER);
  const [saved, setSaved] = useState<number>(DEFAULT_MULTIPLIER);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(ROUTE, { headers: getAuthHeaders(accessToken) });
        if (!res.ok) throw new Error();
        const body = await res.json();
        const value = typeof body.multiplier === 'number' ? body.multiplier : DEFAULT_MULTIPLIER;
        if (live) { setMultiplier(value); setSaved(value); }
      } catch {
        toast.error('Could not load the company-limit multiplier');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [accessToken]);

  const save = async () => {
    if (!isValidMultiplier(multiplier)) {
      toast.error('Multiplier must be a whole number between 1 and 100');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(ROUTE, {
        method: 'POST',
        headers: getAuthHeadersWithJson(accessToken),
        body: JSON.stringify({ multiplier }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not save the multiplier');
      setSaved(body.multiplier ?? multiplier);
      setMultiplier(body.multiplier ?? multiplier);
      toast.success(`Global multiplier updated to ${body.multiplier ?? multiplier}x`);
    } catch (e: any) {
      toast.error(e?.message || 'Could not save the multiplier');
    } finally {
      setSaving(false);
    }
  };

  const changed = multiplier !== saved;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Settings className="size-5" aria-hidden="true" />
          <CardTitle>Company Limit Multiplier</CardTitle>
        </div>
        <CardDescription>System-wide: how many active client companies each user seat allows</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <p className="text-muted-foreground">Loading configuration...</p>
        ) : (
          <>
            <Alert>
              <Info className="size-4" />
              <AlertDescription>
                This applies to every consulting firm. A firm can have its own override, set from Consulting Firms.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="global-multiplier">Company Limit Multiplier</Label>
              <div className="flex gap-2">
                <Input
                  id="global-multiplier"
                  type="number"
                  min={1}
                  max={100}
                  value={multiplier}
                  onChange={(e) => setMultiplier(parseInt(e.target.value, 10) || 1)}
                  className="max-w-xs"
                />
                <Button variant="outline" onClick={() => setMultiplier(DEFAULT_MULTIPLIER)} disabled={multiplier === DEFAULT_MULTIPLIER}>
                  <RotateCcw className="size-4 mr-2" aria-hidden="true" />
                  Reset to {DEFAULT_MULTIPLIER}x
                </Button>
              </div>
              <p className="text-muted-foreground">
                Formula: <code className="bg-muted px-1 rounded">users × multiplier = active companies allowed</code>. The trial is always 1 company.
              </p>
            </div>

            <div className="bg-muted p-4 rounded-lg space-y-2">
              <p>Examples with {multiplier}x:</p>
              <ul className="text-muted-foreground space-y-1" data-testid="multiplier-examples">
                {MULTIPLIER_EXAMPLES.map(({ plan, users }) => (
                  <li key={plan}>
                    {plan} ({users} {users === 1 ? 'user' : 'users'}): {users} × {multiplier} = <strong>{users * multiplier}</strong> companies
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex gap-2">
              <Button onClick={save} disabled={saving || !changed}>
                <Save className="size-4 mr-2" aria-hidden="true" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              {changed && (
                <Button variant="outline" onClick={() => setMultiplier(saved)}>Cancel</Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
